import { z } from "zod/v4";
import { zodAddressWithLocation } from "./location";
import { zodLocales } from "@/app/i18n/settings";
import { PriceIDs, SinglePaymentPriceIDs } from "@/constants/stripeConstants";
import { DataErrorsConst } from "./errors";

export const zodUserData = z.object({
  firstName: z.string().min(2, { message: "First name is required" }).trim(),
  lastName: z.string().min(2, { message: "Last name is required" }).trim(),
  userName: z
    .string()
    .min(3, { message: "Username is required" })
    .regex(/[A-Za-z0-9]*/),
  email: z.string().email({ message: "Email is required" }),
  password: z
    .string()
    .min(8, { message: "Password is required" })
    .regex(/.*[0-9]+.*/)
    .refine((x) => x.toLowerCase() !== x && x.toUpperCase() !== x),
  passwordWdh: z.string().min(8, { message: "Password is required" }),
  dataProtectionAgree: z.literal<boolean>(true),
  language: zodLocales,
});

export const zodStripeSubscriptionId = z.string().min(5).startsWith("sub_");
export const zodStripeCustomerId = z.string().min(5).startsWith("cus_");
export const zodStripeAdvertiserPriceId = z.enum(PriceIDs["AdPartner"]);
export const zodStripePriceID = z.string().min(7).startsWith("price_");
export const zodStripeProductID = z.string().min(7).startsWith("prod_");
export const zodStripePaymentIntentID = z.string().min(7).startsWith("pi_");
export const zodStripeCheckoutSessionID = z.string().min(7).startsWith("cs_");

export const zodPurchasablePriceIDs = z.enum([
  "price_1U5lUHGjbvsJjPaR75iCNjtc",
  "price_1U5lUHGjbvsJjPaRcXVehHNG",
  "price_1ULKaqGjbvsJjPaRFLr7BnOO",
  "price_1ULKarGjbvsJjPaRzUcL1nSM",
  "price_1UQnCSGjbvsJjPaRXDKUOF0j",
] as const satisfies readonly SinglePaymentPriceIDs[]);

export type StripeAdvertiserPriceIDs = z.infer<
  typeof zodStripeAdvertiserPriceId
>;

// Extend with an or if more Payers come
export const zodStripeAllPriceIds = zodStripeAdvertiserPriceId;

export type StripePriceIDs = z.infer<typeof zodStripeAllPriceIds>;

export const zodStripeInterval = z.enum(["day", "week", "month", "year"]);

export const zodInvoiceStepData = z.object({
  billingFirstName: z.string().min(1),
  billingLastName: z.string().min(1),
  billingEmail: z.email(),
  priceId: zodStripeAdvertiserPriceId,
  billingAddress: zodAddressWithLocation,
  testPhase: z.boolean(),
  paymentMethod: z.enum(["immediately", "invoice"]),
  coupon: z.string().optional(),
});

export const zodBillingData = zodInvoiceStepData;

export const zodPhoneNumber = z
  .string()
  .min(5)
  .refine(
    (input) =>
      RegExp(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g).test(input),
    {
      params: {
        i18n: "Telephone",
        namespace: "zodCustomErrors",
      },
    },
  );

export const zodPhoneNumberOptional = z
  .string()
  .optional()
  .refine(
    (input) =>
      input
        ? RegExp(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g).test(input)
        : true,
    {
      params: {
        i18n: "Telephone",
        namespace: "zodCustomErrors",
      },
    },
  );

export const zodBaseCompanyData = z.object({
  companyName: z.string().min(3, { message: DataErrorsConst.MinLength }),
  businessEmail: z.string().email({ message: "not a valid email" }),
  companyAddress: zodAddressWithLocation,
});

/**
 * @deprecated
 * Old Company Data for Register page. Was deprecated to remove unnecessary fields from registration process
 *
 * @constant
 * @name zodOldCompanyData
 * @kind variable
 * @type {z.ZodObject<{ telephone: z.ZodString; cellphone: z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodLiteral<"">]>>; fax: z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodLiteral<"">]>>; description: z.ZodOptional<...>; }, "strip", z.ZodTypeAny, { ...; }, { ...; }>}
 * @exports
 */
export const zodOldCompanyData = zodBaseCompanyData.extend({
  telephone: zodPhoneNumber,
  cellphone: zodPhoneNumber.or(z.literal("")).optional(),
  fax: zodPhoneNumber.or(z.literal("")).optional(),
  description: z.string().max(800).optional(),
});

export const zodBusCompanyData = zodBaseCompanyData.merge(
  z.object({
    phone: zodPhoneNumber,
    fax: zodPhoneNumber.or(z.literal("")).optional(),
  }),
);

export const zodAdvertiserCompanyData = zodBaseCompanyData.merge(
  z.object({
    phone: zodPhoneNumber,
  }),
);

export const zodInvoicePaymentResult = z.object({
  isInvoice: z.literal(true),
  testPhase: z.literal(false),
  amount: z.number().positive().int(),
  subscriptionId: zodStripeSubscriptionId,
  interval: z.enum(["day", "week", "month", "year"]),
  interval_amount: z.number().positive().int(),
  currency: z.enum(["USD", "EUR", "GBP"]),
});

export const zodImmediatePaymentResult = z.object({
  isInvoice: z.literal(false),
  testPhase: z.literal(false),
  amount: z.number().positive().int(),
  subscriptionId: zodStripeSubscriptionId,
  clientSecret: z.string().min(3),
  interval: z.enum(["day", "week", "month", "year"]),
  interval_amount: z.number().positive().int(),
  currency: z.enum(["USD", "EUR", "GBP"]),
});

export const zodTestPhasePaymentResult = z.object({
  isInvoice: z.boolean(),
  testPhase: z.literal(true),
  subscriptionId: zodStripeSubscriptionId,
});

export const zodPaymentResult = zodInvoicePaymentResult
  .or(zodImmediatePaymentResult)
  .or(zodTestPhasePaymentResult);

export const zodCombinedRegisterData = zodBaseCompanyData
  .merge(zodOldCompanyData)
  .merge(zodUserData)
  .merge(zodBillingData);

export type CompanyData = z.infer<typeof zodBaseCompanyData>;
export type OldCompanyData = z.infer<typeof zodOldCompanyData>;
export type UserData = z.infer<typeof zodUserData>;
export type BillingData = z.infer<typeof zodBillingData>;
export type CreatePaymentResult = z.infer<typeof zodPaymentResult>;
export type ImmediatePaymentResult = z.infer<typeof zodImmediatePaymentResult>;
export type InvoicePaymentResult = z.infer<typeof zodInvoicePaymentResult>;

export type InvoiceStepData = z.infer<typeof zodInvoiceStepData>;
export type TestPhasePaymentResult = z.infer<typeof zodTestPhasePaymentResult>;

export type CombinedRegisterData = z.infer<typeof zodCombinedRegisterData>;
